import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, MobileCard, MobileCardRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { UserRound, Key, Link, MoreHorizontal, Edit, Trash, Users, Info, Plus, FileUp } from "lucide-react";
import { getInitials } from "@/lib/utils";

interface Employee {
  id: number;
  fullName: string;
  email: string;
  jobTitle?: string;
  status?: string;
  licenseCount: number;
  teams?: { id: number; name: string }[];
}

interface EmployeesTableProps {
  employees: Employee[];
  filteredEmployees: Employee[];
  paginatedEmployees: Employee[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onViewDetails: (employee: Employee) => void;
  onViewLicenses: (employee: Employee) => void;
  onEdit: (employee: Employee) => void;

  onDelete: (id: number, name: string) => void;
  onAssignLicense: (id: number) => void;
  onCreateEmployee: () => void;
  onImport: () => void;
}

export function EmployeesTableWithPagination({
  employees,
  filteredEmployees,
  paginatedEmployees,
  isLoading,
  page,
  pageSize,
  totalPages,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  onViewLicenses,
  onEdit,
  onDelete,
  onAssignLicense,
  onCreateEmployee,
  onImport
}: EmployeesTableProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (filteredEmployees.length === 0) {
    return (
      <EmptyState
        icon={<UserRound className="h-10 w-10 text-slate-400" />}
        title="No employees found"
        description={employees.length === 0 ? "Add employees to get started" : "Try adjusting your search"}
        action={
          <div className="flex gap-2">
            <Button onClick={onCreateEmployee} variant="default">
              <Plus className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
            <Button onClick={onImport} variant="outline">
              <FileUp className="h-4 w-4 mr-2" />
              Import Employees
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="rounded-md border">
        <Table className="hidden md:table" responsive={false}>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead className="min-w-[200px]">Employee</TableHead>
              <TableHead className="hidden sm:table-cell min-w-[150px]">Email</TableHead>
              <TableHead className="hidden sm:table-cell min-w-[120px]">Teams</TableHead>
              <TableHead className="hidden md:table-cell min-w-[90px]">Status</TableHead>
              <TableHead className="min-w-[100px]">Licenses</TableHead>
              <TableHead className="text-right min-w-[140px] sticky right-0 bg-slate-50 dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedEmployees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell className="min-w-[200px]" truncate>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className="bg-primary text-white text-xs">
                        {getInitials(employee.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <button 
                        className="font-medium text-left hover:text-primary hover:underline focus:outline-none truncate block"
                        onClick={() => onViewDetails(employee)}
                        data-testid={`button-view-employee-${employee.id}`}
                      >
                        {employee.fullName}
                      </button>
                      <div className="text-xs text-slate-500 truncate">{employee.jobTitle || employee.email}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell" truncate>
                  <div className="text-sm truncate">{employee.email}</div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  {employee.teams && employee.teams.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {employee.teams.slice(0, 2).map(team => (
                        <Badge key={team.id} variant="outline" className="text-xs">
                          {team.name}
                        </Badge>
                      ))}
                      {employee.teams.length > 2 && (
                        <Badge variant="outline" className="text-xs">+{employee.teams.length - 2}</Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">No teams</span>
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {(() => {
                    const raw = employee.status || 'Active';
                    const statusMap: Record<string, string> = { active: 'Active', inactive: 'Inactive', onboarding: 'Onboarding', terminated: 'Terminated' };
                    const s = statusMap[raw.toLowerCase()] ?? raw;
                    const colorMap: Record<string, string> = {
                      Active: 'bg-green-100 text-green-800',
                      Inactive: 'bg-slate-100 text-slate-600',
                      Onboarding: 'bg-blue-100 text-blue-800',
                      Terminated: 'bg-red-100 text-red-700',
                    };
                    return <Badge className={`text-xs ${colorMap[s] ?? 'bg-slate-100 text-slate-600'}`}>{s}</Badge>;
                  })()}
                </TableCell>
                <TableCell nowrap>
                  {employee.licenseCount > 0 ? (
                    <div className="flex items-center">
                      <Key className="h-3.5 w-3.5 text-primary mr-1.5" />
                      <span className="text-sm">{employee.licenseCount} licenses</span>
                    </div>
                  ) : (
                    <span className="text-slate-400 text-sm">No licenses</span>
                  )}
                </TableCell>
                <TableCell className="text-right sticky right-0 bg-white dark:bg-slate-900 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">
                  <div className="flex justify-end items-center gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => onAssignLicense(employee.id)}
                            data-testid={`button-assign-license-${employee.id}`}
                          >
                            <Link className="h-4 w-4" />
                            <span className="sr-only">Assign License</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Assign License</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => onViewDetails(employee)}>
                          <Info className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onViewLicenses(employee)}>
                          <Key className="h-4 w-4 mr-2" />
                          View Licenses
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(employee)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Employee
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => onDelete(employee.id, employee.fullName)}
                        >
                          <Trash className="h-4 w-4 mr-2" />
                          Delete Employee
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {/* Mobile Cards */}
        <div className="md:hidden space-y-4 p-4">
          {paginatedEmployees.map((employee) => (
            <MobileCard key={employee.id}>
              <div className="flex items-center space-x-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-primary text-white text-xs">
                    {getInitials(employee.fullName)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <button 
                    className="font-medium text-left hover:text-primary hover:underline focus:outline-none truncate block"
                    onClick={() => onViewDetails(employee)}
                    data-testid={`button-view-employee-mobile-${employee.id}`}
                  >
                    {employee.fullName}
                  </button>
                  <div className="text-xs text-slate-500 truncate">{employee.jobTitle}</div>
                </div>
              </div>
              
              <MobileCardRow label="Email">
                {employee.email}
              </MobileCardRow>

              <MobileCardRow label="Status">
                {(() => {
                  const raw = employee.status || 'Active';
                  const statusMap: Record<string, string> = { active: 'Active', inactive: 'Inactive', onboarding: 'Onboarding', terminated: 'Terminated' };
                  const s = statusMap[raw.toLowerCase()] ?? raw;
                  const colorMap: Record<string, string> = {
                    Active: 'bg-green-100 text-green-800',
                    Inactive: 'bg-slate-100 text-slate-600',
                    Onboarding: 'bg-blue-100 text-blue-800',
                    Terminated: 'bg-red-100 text-red-700',
                  };
                  return <Badge className={`text-xs ${colorMap[s] ?? 'bg-slate-100 text-slate-600'}`}>{s}</Badge>;
                })()}
              </MobileCardRow>

              <MobileCardRow label="Teams">
                {employee.teams && employee.teams.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {employee.teams.map(team => (
                      <Badge key={team.id} variant="outline" className="text-xs">
                        {team.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-slate-400">No teams</span>
                )}
              </MobileCardRow>
              
              <MobileCardRow label="Licenses">
                {employee.licenseCount > 0 ? (
                  <div className="flex items-center">
                    <Key className="h-3.5 w-3.5 text-primary mr-1.5" />
                    {employee.licenseCount} licenses
                  </div>
                ) : (
                  <span className="text-slate-400">No licenses</span>
                )}
              </MobileCardRow>
              
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAssignLicense(employee.id)}
                  data-testid={`button-assign-license-mobile-${employee.id}`}
                >
                  <Link className="h-4 w-4 mr-2" />
                  Assign
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onEdit(employee)}
                  data-testid={`button-edit-employee-mobile-${employee.id}`}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </MobileCard>
          ))}
        </div>
        
        {/* Pagination component */}
        {filteredEmployees.length > 0 && (
          <div className="border-t px-4 bg-slate-50">
            <DataTablePagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filteredEmployees.length}
              onPageChange={onPageChange}
              onPageSizeChange={(size) => {
                onPageSizeChange(size);
                onPageChange(1); // Reset to first page when changing page size
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}